import {
  Alert,
  Box,
  Button,
  Card,
  HStack,
  Separator,
  Spacer,
  Stack,
  Switch,
  Text,
  VStack,
} from "@chakra-ui/react";
import { observer } from "mobx-react";
import { t } from "i18next";
import { useMemo } from "react";
import {
  isHlaAttributeFiltersEmpty,
  isScoreFiltersEmpty,
} from "../../../../map/models/MapFilters";
import { MapColoringSelector } from "../MapColoring/MapColoringSelector";
import { CraAttributeFilters } from "../filters/CraAttributeFilters/CraAttributeFilters";
import { CraScoreFilters } from "../filters/CraScoreFilters";
import { TreatmentFilters } from "../filters/TreatmentFilters";
import { CraForecastControls } from "../CraForecast/CraForecastControls";
import { LifeCycleStageFilters } from "../filters/LifeCycleStageFilters";
import { useStores } from "../../../../shared/stores/RootStore";
import { formatDistanceByUnit } from "../../../../shared/utils/format-utils";
import { LifeCycleStage } from "../../../../cra/models/LifeCycleStage";
import { CraWayFilters } from "../filters/CraWayFilters";
import useAutoResizeMap from "../../../../hooks/useAutoResizeMap";
import { DistanceUnit } from "@keita/lanepatrol-sdk";
import { HlaScoreFilters } from "../filters/HlaScoreFilters";
import { HlaAttributeFilters } from "../filters/HlaAttributeFilters";
import { HlaVerifiedFilters } from "../filters/HlaVerifiedFilters";
import { HlaForecastControls } from "../HlaForecast/HlaForecastControls";
import { MapColoringPieChart } from "../pie-chart/MapColoringPieChart";

const FiltersBase: React.FC = () => {
  useAutoResizeMap();
  const {
    mapStore,
    filterStore,
    authStore,
    craWayStore,
    hlaStore,
    surveyStore,
  } = useStores();
  const selectedCraWay = craWayStore.getSelected();

  const nodes = filterStore.nodesAvailableInCurrentMode;
  const distanceUnit = surveyStore.distanceUnit;

  const totalCraDistance = useMemo(() => {
    return [...nodes.values()].reduce(
      (totalLength, nodes) =>
        totalLength +
        nodes.reduce(
          (routeLength, node) => routeLength + node.distanceToNext,
          0,
        ),
      0,
    );
  }, [nodes]);

  const totalHlaDistance = useMemo(() => {
    return hlaStore.ways.reduce((acc, way) => {
      const nOfLanes = [way.frontFacingLane, way.backFacingLane].reduce(
        (total, lane) => {
          return lane ? total + 1 : total;
        },
        0,
      );
      return acc + way.lengthM * nOfLanes;
    }, 0);
  }, [hlaStore.ways]);

  const selectedStage = selectedCraWay?.lifeCycleStage;
  const codingNotDone =
    selectedStage === LifeCycleStage.Design ||
    selectedStage === LifeCycleStage.DataCollection ||
    selectedStage === LifeCycleStage.Coding;

  const DisplayDistance = ({
    filtered,
    total,
  }: {
    filtered: number;
    total: number;
  }) => (
    <Box>
      <Text fontSize="xs" fontWeight="semibold">
        {t("map:filters.distanceLabel")}
      </Text>

      <Text fontSize="sm">
        {t("map:filters.distanceSummary", {
          filtered: formatDistanceByUnit(filtered, distanceUnit),
          total: formatDistanceByUnit(total, distanceUnit),
          unit: distanceUnit === DistanceUnit.Imperial ? "mi" : "km",
        })}
      </Text>
    </Box>
  );

  return (
    <VStack
      overflowY="scroll"
      gap={4}
      py={4}
      px={4}
      alignItems="stretch"
      w={320}
      flexShrink={0}
    >
      {craWayStore.ways.length > 0 && (
        <Stack gap={4}>
          <HStack>
            <Switch.Root
              colorPalette={"primary"}
              checked={filterStore.showCra}
              onCheckedChange={({ checked }) => filterStore.setShowCra(checked)}
            >
              <Switch.HiddenInput />
              <Switch.Control>
                <Switch.Thumb />
              </Switch.Control>
              <Switch.Label>CycleRAP Assessments</Switch.Label>
            </Switch.Root>
          </HStack>

          {filterStore.showCra && (
            <>
              <Card.Root>
                <Stack gap={2} p={2}>
                  <HStack>
                    <Text fontSize="sm">Filters</Text>
                    <Spacer />
                    {!(
                      isScoreFiltersEmpty(filterStore.craScoreFilters) &&
                      !filterStore.craAttributeFilters.length &&
                      !filterStore.craWayFilters.length &&
                      !filterStore.lifeCycleStageFilters.length &&
                      !filterStore.treatmentFilters.length
                    ) && (
                      <Button
                        m={-2}
                        size="sm"
                        fontSize={"sm"}
                        colorPalette="primary"
                        variant="ghost"
                        onClick={() => {
                          filterStore.resetCraFilters();
                        }}
                        title={t(`map:filters.clearAllTitle`)}
                      >
                        {t(`map:filters.clearAllTitle`)}
                      </Button>
                    )}
                  </HStack>

                  <Stack flexDir={"row"} flexWrap={"wrap"} alignItems={"start"}>
                    <CraWayFilters />
                    {authStore.isAuthenticated && <LifeCycleStageFilters />}
                    <TreatmentFilters />
                    <CraScoreFilters />
                    <CraAttributeFilters />
                  </Stack>

                  <DisplayDistance
                    filtered={filterStore.filteredCraDistance}
                    total={totalCraDistance}
                  />

                  <Separator mx={-2} />

                  {!codingNotDone ? (
                    <CraForecastControls />
                  ) : (
                    <Alert.Root>
                      <Alert.Indicator />
                      <Alert.Description>
                        {t(`map:filters.forecastUnavailable`)}
                      </Alert.Description>
                    </Alert.Root>
                  )}
                </Stack>
              </Card.Root>
            </>
          )}
        </Stack>
      )}

      {hlaStore.ways.length > 0 && (
        <Stack gap={4}>
          <HStack>
            <Switch.Root
              colorPalette={"primary"}
              checked={filterStore.showHla}
              onCheckedChange={({ checked }) => filterStore.setShowHla(checked)}
            >
              <Switch.HiddenInput />
              <Switch.Control>
                <Switch.Thumb />
              </Switch.Control>
              <Switch.Label>High-level Assessments</Switch.Label>
            </Switch.Root>
          </HStack>

          {filterStore.showHla && (
            <>
              <Card.Root>
                <Stack gap={2} p={2}>
                  <HStack>
                    <Text fontSize="sm">Filters</Text>
                    <Spacer />

                    {!(
                      isScoreFiltersEmpty(filterStore.hlaScoreFilters) &&
                      isHlaAttributeFiltersEmpty(
                        filterStore.hlaAttributeFilters,
                      ) &&
                      !filterStore.hlaVerifiedFilters.length
                    ) && (
                      <Button
                        m={-2}
                        size="sm"
                        fontSize={"sm"}
                        colorPalette="primary"
                        variant="ghost"
                        onClick={() => {
                          filterStore.resetHlaFilters();
                        }}
                        title={t(`map:filters.clearAllTitle`)}
                      >
                        {t(`map:filters.clearAllTitle`)}
                      </Button>
                    )}
                  </HStack>
                  <Stack flexDir={"row"} flexWrap={"wrap"} alignItems={"start"}>
                    <HlaVerifiedFilters />
                    <HlaScoreFilters />
                    <HlaAttributeFilters />
                  </Stack>

                  <DisplayDistance
                    filtered={filterStore.filteredHlaDistance}
                    total={totalHlaDistance}
                  />

                  <Separator mx={-2} />

                  <HlaForecastControls />
                </Stack>
              </Card.Root>
            </>
          )}
        </Stack>
      )}

      <Stack gap={0}>
        <HStack gap={0}>
          <Text fontSize={"sm"} flexShrink={0}>
            Line coloring
          </Text>

          <MapColoringSelector
            value={mapStore.coloring}
            onChange={(coloring) => mapStore.setColoring({ ...coloring })}
          />
        </HStack>

        <MapColoringPieChart coloring={mapStore.coloring} />
      </Stack>
    </VStack>
  );
};

export const FiltersBar = observer(FiltersBase);
